import {
  addDoc,
  collection,
  deleteField,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { normalizeIdArray } from '../utils/collectionUtils';
import {
  getPrimaryContactMethod,
  normalizeContactMethods,
} from '../utils/contactMethods';
import {
  buildSupplierRestoreUpdates,
  buildSupplierSoftDeleteUpdates,
} from './supplierDeletionPayloads';

const parsePriceHistoryDate = (value) => {
  if (!value) {
    return null;
  }

  const parsedDate =
    typeof value?.toDate === 'function'
      ? value.toDate()
      : value instanceof Date
        ? value
        : new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export const sortPriceHistoryByDateDesc = (priceHistory) =>
  [...(Array.isArray(priceHistory) ? priceHistory : [])].sort((firstEntry, secondEntry) => {
    const firstDate = parsePriceHistoryDate(firstEntry?.date);
    const secondDate = parsePriceHistoryDate(secondEntry?.date);
    const firstTime = firstDate ? firstDate.getTime() : Number.NEGATIVE_INFINITY;
    const secondTime = secondDate ? secondDate.getTime() : Number.NEGATIVE_INFINITY;

    return secondTime - firstTime;
  });

export const updateItemStatus = async (collectionName, id, updates) => {
  await updateDoc(doc(db, collectionName, id), updates);
};

export const deleteItem = async (collectionName, id) => {
  await deleteDoc(doc(db, collectionName, id));
};

const generateSlug = (name = '') =>
  name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');

const PRODUCT_TYPES = ['simple', 'variant', 'mixed'];

const normalizeProductType = (value) =>
  PRODUCT_TYPES.includes(value) ? value : 'simple';

export const MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS = [
  { value: 'width', label: 'Width' },
  { value: 'height', label: 'Height' },
  { value: 'length', label: 'Length' },
  { value: 'quantity', label: 'Quantity' },
];

const MIXED_PRODUCT_REQUIRED_INPUT_VALUES =
  MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS.map((option) => option.value);

export const normalizeMixedProductRequiredInputs = (value) => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];

  return Array.from(
    new Set(
      rawValues
        .map((item) => normalizeString(item).toLowerCase())
        .filter((item) => MIXED_PRODUCT_REQUIRED_INPUT_VALUES.includes(item))
    )
  );
};

export const getMixedProductRequiredInputLabel = (value) =>
  MIXED_PRODUCT_REQUIRED_INPUT_OPTIONS.find((option) => option.value === value)
    ?.label || '';

export const MIXED_PRODUCT_CALCULATION_MODE_OPTIONS = [
  { value: 'fixed', label: 'Fixed' },
  { value: 'area_wh', label: 'Area (Width x Height)' },
  { value: 'area_wl', label: 'Area (Width x Length)' },
  { value: 'volume_whl', label: 'Volume (Width x Height x Length)' },
];

const MIXED_PRODUCT_CALCULATION_MODE_VALUES =
  MIXED_PRODUCT_CALCULATION_MODE_OPTIONS.map((option) => option.value);

export const normalizeMixedProductCalculationMode = (value) =>
  MIXED_PRODUCT_CALCULATION_MODE_VALUES.includes(value) ? value : 'fixed';

export const getMixedProductCalculationModeLabel = (value) =>
  MIXED_PRODUCT_CALCULATION_MODE_OPTIONS.find(
    (option) => option.value === normalizeMixedProductCalculationMode(value)
  )?.label || 'Fixed';

export const getMixedProductCalculationModeRequiredInputs = (value) => {
  const calculationMode = normalizeMixedProductCalculationMode(value);

  if (calculationMode === 'area_wh') {
    return ['width', 'height'];
  }

  if (calculationMode === 'area_wl') {
    return ['width', 'length'];
  }

  if (calculationMode === 'volume_whl') {
    return ['width', 'height', 'length'];
  }

  return [];
};

export const DEFAULT_PRICING_METHOD = 'perUnit';

export const PRICING_METHOD_OPTIONS = [
  { value: 'perUnit', label: 'Per Unit' },
  { value: 'perSquareMeter', label: 'Per Square Meter' },
];

const PRICING_METHOD_VALUES = PRICING_METHOD_OPTIONS.map((option) => option.value);

export const LEGACY_PRICING_METHODS = {
  fixedPrice: {
    label: 'Fixed Total',
    normalizedMethod: 'perUnit',
    handling: 'Normalized to Per Unit',
    unsupported: false,
  },
  perMeter: {
    label: 'Per Meter',
    normalizedMethod: 'perMeter',
    handling: 'Legacy unsupported - requires manual review',
    unsupported: true,
  },
  perCubicMeter: {
    label: 'Per Cubic Meter',
    normalizedMethod: 'perCubicMeter',
    handling: 'Legacy unsupported - requires manual review',
    unsupported: true,
  },
  perDay: {
    label: 'Per Day',
    normalizedMethod: 'perDay',
    handling: 'Legacy unsupported - requires manual review',
    unsupported: true,
  },
  perPerson: {
    label: 'Per Person',
    normalizedMethod: 'perPerson',
    handling: 'Legacy unsupported - requires manual review',
    unsupported: true,
  },
};

export const normalizePricingMethod = (value) => {
  const normalizedValue = normalizeString(value);

  if (PRICING_METHOD_VALUES.includes(normalizedValue)) {
    return normalizedValue;
  }

  return LEGACY_PRICING_METHODS[normalizedValue]?.normalizedMethod ||
    DEFAULT_PRICING_METHOD;
};

export const normalizeEditablePricingMethod = (value) => {
  const normalizedMethod = normalizePricingMethod(value);

  return PRICING_METHOD_VALUES.includes(normalizedMethod)
    ? normalizedMethod
    : DEFAULT_PRICING_METHOD;
};

const normalizeWritablePricingMethod = (value) => {
  const rawValue = normalizeString(value);
  const normalizedMethod = normalizePricingMethod(rawValue);

  if (PRICING_METHOD_VALUES.includes(normalizedMethod)) {
    return normalizedMethod;
  }

  const legacyMethod = LEGACY_PRICING_METHODS[rawValue] ||
    LEGACY_PRICING_METHODS[normalizedMethod];

  throw new Error(
    `${legacyMethod?.label || 'Legacy pricing type'} is a legacy pricing type. Choose Per Unit or Per Square Meter.`
  );
};

export const getPricingMethodLabel = (value) =>
  PRICING_METHOD_OPTIONS.find(
    (option) => option.value === normalizePricingMethod(value)
  )?.label ||
  (LEGACY_PRICING_METHODS[normalizeString(value)]?.unsupported
    ? `Legacy: ${LEGACY_PRICING_METHODS[normalizeString(value)].label}`
    : 'Per Unit');

export const getPricingMethodNormalizationDetails = (value) => {
  const rawValue = normalizeString(value);
  const normalizedMethod = normalizePricingMethod(rawValue);
  const supportedOption = PRICING_METHOD_OPTIONS.find(
    (option) => option.value === normalizedMethod
  );
  const legacyMethod = LEGACY_PRICING_METHODS[rawValue];

  return {
    rawMethod: rawValue || '',
    normalizedMethod,
    label: supportedOption?.label || legacyMethod?.label || 'Per Unit',
    handling: legacyMethod?.handling || 'Supported',
    isLegacy: Boolean(legacyMethod),
    isUnsupportedLegacy: legacyMethod?.unsupported === true,
  };
};

export const buildPricingMethodNormalizationReport = (recordsByCollection = {}) => {
  const report = {
    totalRecords: 0,
    supportedRecords: 0,
    fixedTotalNormalizedToPerUnit: 0,
    unsupportedLegacyRecords: 0,
    unsupportedLegacyByMethod: {},
  };

  Object.values(recordsByCollection || {}).forEach((records) => {
    (Array.isArray(records) ? records : []).forEach((record) => {
      if (!record || typeof record !== 'object') {
        return;
      }

      report.totalRecords += 1;
      const details = getPricingMethodNormalizationDetails(record.pricingMethod);

      if (details.rawMethod === 'fixedPrice') {
        report.fixedTotalNormalizedToPerUnit += 1;
      }

      if (details.isUnsupportedLegacy) {
        report.unsupportedLegacyRecords += 1;
        report.unsupportedLegacyByMethod[details.rawMethod] =
          (report.unsupportedLegacyByMethod[details.rawMethod] || 0) + 1;
        return;
      }

      report.supportedRecords += 1;
    });
  });

  return report;
};

export const normalizeVariantNames = (value) => {
  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [];

  return Array.from(
    new Set(
      rawValues
        .map((item) => normalizeString(item))
        .filter(Boolean)
    )
  );
};

const normalizeProduct = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};
  const name = typeof data?.name === 'string' ? data.name.trim() : '';
  const description =
    typeof data?.description === 'string' ? data.description.trim() : '';

  return {
    id: docSnapshot?.id || data.id,
    name,
    description,
    type: normalizeProductType(data?.type),
    allowedVariants: normalizeVariantNames(data?.allowedVariants),
    requiredInputs:
      normalizeProductType(data?.type) === 'mixed'
        ? normalizeMixedProductRequiredInputs(data?.requiredInputs)
        : [],
    calculationMode:
      normalizeProductType(data?.type) === 'mixed'
        ? normalizeMixedProductCalculationMode(data?.calculationMode)
        : 'fixed',
    categories: normalizeIdArray(data?.categories),
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
    createdAt: data?.createdAt ?? null,
    updatedAt: data?.updatedAt ?? null,
  };
};

const normalizeProductVariant = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};

  return {
    id: docSnapshot?.id || data.id,
    productId: normalizeString(data?.productId),
    supplierId: normalizeString(data?.supplierId),
    productSupplierId: normalizeString(data?.productSupplierId),
    variantName: normalizeString(data?.variantName || data?.name),
    name: normalizeString(data?.name || data?.variantName),
    currentPrice: data?.currentPrice ?? null,
    lastUpdated: data?.lastUpdated ?? null,
    notes: normalizeString(data?.notes || data?.note),
    specs: data?.specs && typeof data.specs === 'object' ? data.specs : {},
    priceHistory: Array.isArray(data?.priceHistory) ? data.priceHistory : [],
    isActive: data?.isActive === true,
    createdAt: data?.createdAt ?? null,
  };
};

const normalizeString = (value) =>
  typeof value === 'string' ? value.trim() : '';

const normalizeNumber = (value, fallback = 0) => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
};

const normalizeProductComponent = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};

  return {
    id: docSnapshot?.id || data.id,
    productId: normalizeString(data?.productId),
    name: normalizeString(data?.name),
    description: normalizeString(data?.description),
    quantity: normalizeNumber(data?.quantity, 1),
    unit: normalizeString(data?.unit),
    sourceProductId: normalizeString(data?.sourceProductId),
    sourceProductName: normalizeString(data?.sourceProductName),
    sourceVariantId: normalizeString(data?.sourceVariantId),
    sourceVariantName: normalizeString(data?.sourceVariantName),
    sortOrder: normalizeNumber(data?.sortOrder, 0),
    isRequired: data?.isRequired === true,
    selectedComponentSupplierId: normalizeString(data?.selectedComponentSupplierId),
    selectedSupplierId: normalizeString(data?.selectedSupplierId),
    selectedUnitPrice: data?.selectedUnitPrice ?? null,
    lineTotal: data?.lineTotal ?? null,
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
    createdAt: data?.createdAt ?? null,
    updatedAt: data?.updatedAt ?? null,
  };
};

const normalizeComponentSupplier = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};

  return {
    id: docSnapshot?.id || data.id,
    componentId: normalizeString(data?.componentId),
    supplierId: normalizeString(data?.supplierId),
    currentPrice: data?.currentPrice ?? null,
    unit: normalizeString(data?.unit),
    pricingMethod: normalizePricingMethod(data?.pricingMethod),
    lastUpdated: data?.lastUpdated ?? null,
    notes: normalizeString(data?.notes || data?.note),
    isActive: data?.isActive === true,
    createdAt: data?.createdAt ?? null,
    updatedAt: data?.updatedAt ?? null,
  };
};

const normalizeComponentPriceHistoryEntry = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};

  return {
    id: docSnapshot?.id || data.id,
    componentSupplierId: normalizeString(data?.componentSupplierId),
    componentId: normalizeString(data?.componentId),
    supplierId: normalizeString(data?.supplierId),
    price: data?.price ?? null,
    unit: normalizeString(data?.unit),
    pricingMethod: normalizePricingMethod(data?.pricingMethod),
    date: data?.date ?? null,
    note: normalizeString(data?.note || data?.notes),
    createdAt: data?.createdAt ?? null,
  };
};

const normalizeSpecs = (specs) => {
  if (!specs || typeof specs !== 'object') {
    return {};
  }

  if (Array.isArray(specs)) {
    return specs.reduce((accumulator, spec) => {
      const key = normalizeString(spec?.key);
      const value = normalizeString(spec?.value);

      if (key && value) {
        accumulator[key] = value;
      }

      return accumulator;
    }, {});
  }

  return Object.entries(specs).reduce((accumulator, [key, value]) => {
    const normalizedKey = normalizeString(key);
    const normalizedValue = normalizeString(value);

    if (normalizedKey && normalizedValue) {
      accumulator[normalizedKey] = normalizedValue;
    }

    return accumulator;
  }, {});
};

const normalizeSocials = (data = {}) => {
  const socials = data?.socials && typeof data.socials === 'object' ? data.socials : {};

  return {
    facebook: normalizeString(socials.facebook || data?.facebook),
    instagram: normalizeString(socials.instagram || data?.instagram),
    tiktok: normalizeString(socials.tiktok || data?.tiktok),
    linkedin: normalizeString(socials.linkedin || data?.linkedin),
    telegram: normalizeString(socials.telegram || data?.telegram),
  };
};

const normalizeSupplier = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};
  const contactMethods = normalizeContactMethods(data);
  const primaryContactMethod = getPrimaryContactMethod(contactMethods);
  const companyPhone = primaryContactMethod?.number || '';
  const companyEmail =
    typeof data?.companyEmail === 'string' && data.companyEmail.trim()
      ? data.companyEmail.trim()
      : typeof data?.email === 'string'
        ? data.email.trim()
        : '';

  return {
    id: docSnapshot?.id || data.id,
    companyName: normalizeString(data?.companyName || data?.name),
    contactMethods,
    companyPhone,
    phone: companyPhone,
    companyWhatsapp: primaryContactMethod?.hasWhatsapp ? primaryContactMethod.number : '',
    companyEmail,
    email: companyEmail,
    website: normalizeString(data?.website),
    address: normalizeString(data?.address),
    mapsLink: normalizeString(data?.mapsLink),
    socials: normalizeSocials(data),
    comments: normalizeString(data?.comments),
    categories: normalizeIdArray(data?.categories),
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
    createdAt: data?.createdAt ?? null,
  };
};

const mapCountSnapshot = (snapshot) => snapshot.data().count;

const mapDocumentSnapshot = (docSnapshot) => ({
  id: docSnapshot.id,
  ...docSnapshot.data(),
});

export const USER_ROLES = ['owner', 'admin', 'manager', 'staff'];

const normalizeUserRole = (role) =>
  USER_ROLES.includes(role) ? role : 'staff';

const normalizeUser = (docSnapshot) => {
  const data = docSnapshot?.data ? docSnapshot.data() : docSnapshot || {};
  const rawStatus = typeof data?.status === 'string'
    ? data.status.trim().toLowerCase()
    : '';
  const status = rawStatus === 'invited'
    ? 'invited'
    : data?.isActive === false
      ? 'inactive'
      : 'active';

  return {
    id: docSnapshot?.id || data.uid || data.id,
    uid: typeof data?.uid === 'string' ? data.uid : docSnapshot?.id || '',
    name: typeof data?.name === 'string' ? data.name.trim() : '',
    email: typeof data?.email === 'string' ? data.email.trim().toLowerCase() : '',
    role: normalizeUserRole(data?.role),
    status,
    isActive: data?.isActive !== false,
    createdAt: data?.createdAt ?? null,
  };
};

const buildProductPayload = (
  data = {},
  { includeCreatedAt = false, removeLegacyFields = false } = {}
) => {
  const type = normalizeProductType(data?.type);
  const payload = {
    name: typeof data?.name === 'string' ? data.name.trim() : '',
    description: typeof data?.description === 'string' ? data.description.trim() : '',
    type,
    allowedVariants:
      type === 'variant' ? normalizeVariantNames(data?.allowedVariants) : [],
    requiredInputs:
      type === 'mixed' ? normalizeMixedProductRequiredInputs(data?.requiredInputs) : [],
    calculationMode:
      type === 'mixed'
        ? normalizeMixedProductCalculationMode(data?.calculationMode)
        : 'fixed',
    categories: normalizeIdArray(data?.categories),
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
    updatedAt: serverTimestamp(),
  };

  if (removeLegacyFields) {
    payload.productName = deleteField();
  }

  if (includeCreatedAt) {
    payload.createdAt = serverTimestamp();
  }

  return payload;
};

const buildSupplierPayload = (
  data = {},
  { includeCreatedAt = false, removeLegacyFields = false } = {}
) => {
  const companyName = normalizeString(data?.companyName || data?.name);
  const contactMethods = normalizeContactMethods(data);
  const companyEmail =
    typeof data?.companyEmail === 'string' && data.companyEmail.trim()
      ? data.companyEmail.trim()
      : typeof data?.email === 'string'
        ? data.email.trim()
        : '';
  const socials = normalizeSocials(data);

  const payload = {
    companyName,
    name: companyName,
    contactMethods,
    companyEmail,
    email: companyEmail,
    website: normalizeString(data?.website),
    address: normalizeString(data?.address),
    mapsLink: normalizeString(data?.mapsLink),
    socials,
    comments: normalizeString(data?.comments),
    categories: normalizeIdArray(data?.categories),
    isDeleted: data?.isDeleted === true,
    deletedAt: data?.deletedAt ?? null,
  };

  if (includeCreatedAt) {
    payload.createdAt = data?.createdAt ?? serverTimestamp();
  }

  if (removeLegacyFields) {
    payload.companyPhone = deleteField();
    payload.phone = deleteField();
    payload.companyWhatsapp = deleteField();
    payload.whatsapp = deleteField();
  }

  return payload;
};

const getCurrentAuditActor = async () => {
  const currentUser = auth?.currentUser;

  if (!currentUser?.uid) {
    return null;
  }

  let accessRecord = null;

  try {
    accessRecord = await getUserAccess(currentUser.uid, currentUser.email);
  } catch {
    accessRecord = null;
  }

  const displayName = normalizeString(
    accessRecord?.name || currentUser.displayName || currentUser.email
  );
  const email = normalizeString(accessRecord?.email || currentUser.email).toLowerCase();
  const role = normalizeString(accessRecord?.role);

  return {
    uid: currentUser.uid,
    name: displayName,
    displayName,
    email,
    role,
    source: 'user',
  };
};

const withAuditActor = async (payload, fieldName = 'updatedBy') => {
  const actor = await getCurrentAuditActor();

  return actor
    ? {
      ...payload,
      [fieldName]: actor,
    }
    : payload;
};

export const getCategories = async () => {
  const q = query(collection(db, 'categories'), where('isDeleted', '==', false));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  }));
};

export const getAllCategories = async () => {
  const querySnapshot = await getDocs(collection(db, 'categories'));

  return querySnapshot.docs.map((docSnapshot) => mapDocumentSnapshot(docSnapshot));
};

export const getUsers = async () => {
  const querySnapshot = await getDocs(collection(db, 'users'));

  return querySnapshot.docs.map((docSnapshot) => normalizeUser(docSnapshot));
};

export const getProductVariants = async () => {
  const querySnapshot = await getDocs(collection(db, 'productVariants'));

  return querySnapshot.docs.map((docSnapshot) => normalizeProductVariant(docSnapshot));
};

export const getSupplierContacts = async () => {
  const querySnapshot = await getDocs(collection(db, 'supplierContacts'));

  return querySnapshot.docs.map((docSnapshot) => ({
    ...mapDocumentSnapshot(docSnapshot),
    contactMethods: normalizeContactMethods(docSnapshot.data(), {
      numberKeys: ['contactNumber', 'phone'],
      whatsappKeys: ['contactWhatsapp', 'whatsapp'],
      defaultLabel: 'Mobile',
    }),
  }));
};

export const getPriceHistory = async () => {
  const querySnapshot = await getDocs(collection(db, 'priceHistory'));

  return querySnapshot.docs.map((docSnapshot) => mapDocumentSnapshot(docSnapshot));
};

export const getAllProductSuppliers = async () => {
  const querySnapshot = await getDocs(collection(db, 'productSuppliers'));

  return querySnapshot.docs.map((docSnapshot) => mapDocumentSnapshot(docSnapshot));
};

export const getAllProductComponents = async () => {
  const querySnapshot = await getDocs(collection(db, 'productComponents'));

  return querySnapshot.docs.map((docSnapshot) => normalizeProductComponent(docSnapshot));
};

export const getAllComponentSuppliers = async () => {
  const querySnapshot = await getDocs(collection(db, 'componentSuppliers'));

  return querySnapshot.docs.map((docSnapshot) => mapDocumentSnapshot(docSnapshot));
};

export const getAllComponentPriceHistory = async () => {
  const querySnapshot = await getDocs(collection(db, 'componentPriceHistory'));

  return querySnapshot.docs.map((docSnapshot) => mapDocumentSnapshot(docSnapshot));
};

export const getDashboardSummary = async () => {
  const [supplierCount, productCount, categoryCount] = await Promise.all([
    getCountFromServer(
      query(collection(db, 'suppliers'), where('isDeleted', '==', false))
    ),
    getCountFromServer(
      query(collection(db, 'products'), where('isDeleted', '==', false))
    ),
    getCountFromServer(
      query(collection(db, 'categories'), where('isDeleted', '==', false))
    ),
  ]);

  return {
    suppliers: mapCountSnapshot(supplierCount),
    products: mapCountSnapshot(productCount),
    categories: mapCountSnapshot(categoryCount),
  };
};

export const getUserAccessByEmail = async (email) => {
  const usersQuery = query(
    collection(db, 'users'),
    where('email', '==', email),
    limit(1)
  );
  const querySnapshot = await getDocs(usersQuery);

  if (querySnapshot.empty) {
    return null;
  }

  return normalizeUser(querySnapshot.docs[0]);
};

export const getUserAccess = async (uid, email) => {
  if (uid) {
    const userSnapshot = await getDoc(doc(db, 'users', uid));

    if (userSnapshot.exists()) {
      return normalizeUser(userSnapshot);
    }
  }

  return getUserAccessByEmail(email);
};

export const getSupplierById = async (id) => {
  const docSnapshot = await getDoc(doc(db, 'suppliers', id));

  if (!docSnapshot.exists()) {
    return null;
  }

  return normalizeSupplier(docSnapshot);
};

export const getSuppliersByIds = async (ids) => {
  const supplierIds = Array.from(
    new Set(
      (Array.isArray(ids) ? ids : [])
        .map((id) => normalizeString(id))
        .filter(Boolean)
    )
  );
  const suppliers = await Promise.all(supplierIds.map((id) => getSupplierById(id)));

  return suppliers.filter(Boolean);
};

export const getSuppliers = async () => {
  const querySnapshot = await getDocs(collection(db, 'suppliers'));

  return querySnapshot.docs.map((docSnapshot) => normalizeSupplier(docSnapshot));
};

export const getRecentSuppliers = async (maxResults = 5) => {
  const querySnapshot = await getDocs(
    query(
      collection(db, 'suppliers'),
      orderBy('createdAt', 'desc'),
      limit(Math.max(maxResults * 3, 12))
    )
  );

  return querySnapshot.docs
    .map((docSnapshot) => normalizeSupplier(docSnapshot))
    .filter((supplier) => supplier.isDeleted !== true)
    .slice(0, maxResults);
};

export const createSupplier = async (data) => {
  const payload = await withAuditActor(
    buildSupplierPayload(data, { includeCreatedAt: true }),
    'createdBy'
  );
  const docRef = await addDoc(collection(db, 'suppliers'), payload);

  return normalizeSupplier({
    id: docRef.id,
    ...payload,
    createdAt: new Date(),
  });
};

export const updateSupplier = async (id, data) => {
  const payload = await withAuditActor(
    buildSupplierPayload(data, { removeLegacyFields: true })
  );

  await updateDoc(
    doc(db, 'suppliers', id),
    payload
  );

  const updatedSnapshot = await getDoc(doc(db, 'suppliers', id));

  if (!updatedSnapshot.exists()) {
    return null;
  }

  return normalizeSupplier(updatedSnapshot);
};

export const softDeleteSupplier = async (id) => {
  console.info('SOFT DELETE PATH', { supplierId: id });
  await updateDoc(
    doc(db, 'suppliers', id),
    await withAuditActor(buildSupplierSoftDeleteUpdates(serverTimestamp()))
  );
};

export const restoreSupplier = async (id) => {
  console.info('RESTORE PATH', { supplierId: id });
  await updateDoc(
    doc(db, 'suppliers', id),
    await withAuditActor(buildSupplierRestoreUpdates())
  );
};

export const permanentlyDeleteSupplier = async (id) => {
  console.info('PERMANENT DELETE PATH', { supplierId: id });
  const deleteSupplier = httpsCallable(functions, 'deleteSupplier');
  const result = await deleteSupplier({ supplierId: id, permanent: true });

  return result.data;
};

export const getProductById = async (id) => {
  const docSnapshot = await getDoc(doc(db, 'products', id));

  if (!docSnapshot.exists()) {
    return null;
  }

  return normalizeProduct(docSnapshot);
};

export const getProducts = async () => {
  const querySnapshot = await getDocs(collection(db, 'products'));

  return querySnapshot.docs.map((docSnapshot) => normalizeProduct(docSnapshot));
};

export const getRecentProducts = async (maxResults = 5) => {
  const querySnapshot = await getDocs(
    query(
      collection(db, 'products'),
      orderBy('createdAt', 'desc'),
      limit(Math.max(maxResults * 3, 12))
    )
  );

  return querySnapshot.docs
    .map((docSnapshot) => normalizeProduct(docSnapshot))
    .filter((product) => product.isDeleted !== true)
    .slice(0, maxResults);
};

export const getActiveProductVariantsByProductId = async (productId) => {
  if (!productId) {
    return [];
  }

  const q = query(
    collection(db, 'productVariants'),
    where('productId', '==', productId),
    where('isActive', '==', true)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => normalizeProductVariant(docSnapshot));
};

export const getActiveProductVariantsByProductAndSupplierId = async (
  productId,
  supplierId
) => {
  if (!productId || !supplierId) {
    return [];
  }

  const q = query(
    collection(db, 'productVariants'),
    where('productId', '==', productId),
    where('supplierId', '==', supplierId),
    where('isActive', '==', true)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => normalizeProductVariant(docSnapshot));
};

const validateMixedProduct = async (productId) => {
  const normalizedProductId = normalizeString(productId);

  if (!normalizedProductId) {
    throw new Error('Mixed product is required.');
  }

  const productSnapshot = await getDoc(doc(db, 'products', normalizedProductId));

  if (!productSnapshot.exists()) {
    throw new Error('Product not found.');
  }

  const product = normalizeProduct(productSnapshot);

  if (product.type !== 'mixed') {
    throw new Error('Components can only be added to mixed products.');
  }

  return product;
};

const getProductComponentById = async (id) => {
  const componentSnapshot = await getDoc(doc(db, 'productComponents', id));

  if (!componentSnapshot.exists()) {
    return null;
  }

  return normalizeProductComponent(componentSnapshot);
};

const validateComponentBelongsToMixedProduct = async (componentId) => {
  const normalizedComponentId = normalizeString(componentId);

  if (!normalizedComponentId) {
    throw new Error('Component is required.');
  }

  const component = await getProductComponentById(normalizedComponentId);

  if (!component) {
    throw new Error('Component not found.');
  }

  if (component.isDeleted) {
    throw new Error('Component is deleted.');
  }

  await validateMixedProduct(component.productId);

  return component;
};

const validateSupplierExists = async (supplierId) => {
  const normalizedSupplierId = normalizeString(supplierId);

  if (!normalizedSupplierId) {
    throw new Error('Supplier is required.');
  }

  const supplierSnapshot = await getDoc(doc(db, 'suppliers', normalizedSupplierId));

  if (!supplierSnapshot.exists()) {
    throw new Error('Supplier not found.');
  }

  return normalizedSupplierId;
};

const getActiveComponentSupplierByComponentAndSupplierId = async (
  componentId,
  supplierId
) => {
  const q = query(
    collection(db, 'componentSuppliers'),
    where('componentId', '==', componentId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map((docSnapshot) => normalizeComponentSupplier(docSnapshot))
    .find((componentSupplier) =>
      componentSupplier.componentId === componentId &&
      componentSupplier.supplierId === supplierId &&
      componentSupplier.isActive === true
    ) || null;
};

const getActiveComponentSuppliersByComponentId = async (componentId) => {
  const q = query(
    collection(db, 'componentSuppliers'),
    where('componentId', '==', componentId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map((docSnapshot) => normalizeComponentSupplier(docSnapshot))
    .filter((componentSupplier) => componentSupplier.componentId === componentId)
    .filter((componentSupplier) => componentSupplier.isActive === true);
};

const hasValidComponentSupplierCurrentPrice = (componentSupplier) => {
  const price = Number(componentSupplier?.currentPrice);

  return Number.isFinite(price) && price > 0;
};

const getSelectedComponentSupplierFromRows = (component, componentSuppliers) => {
  const selectedComponentSupplierId = normalizeString(
    component?.selectedComponentSupplierId
  );
  const selectedSupplierId = normalizeString(component?.selectedSupplierId);
  const componentSupplierRows = Array.isArray(componentSuppliers)
    ? componentSuppliers
    : [];
  const selectedById = componentSupplierRows.find(
    (componentSupplier) =>
      selectedComponentSupplierId &&
      componentSupplier?.id === selectedComponentSupplierId
  );

  if (selectedById) {
    return selectedById;
  }

  return componentSupplierRows.find(
    (componentSupplier) =>
      selectedSupplierId &&
      componentSupplier?.supplierId === selectedSupplierId
  ) || null;
};

const buildComponentSelectionPayload = (component, componentSuppliers) => {
  const selectedSupplier = getSelectedComponentSupplierFromRows(
    component,
    componentSuppliers
  );
  const selectedUnitPrice = hasValidComponentSupplierCurrentPrice(selectedSupplier)
    ? Number(selectedSupplier.currentPrice)
    : null;
  const quantity = normalizeNumber(component?.quantity, 0);
  const pricingMethod = normalizePricingMethod(selectedSupplier?.pricingMethod);

  return {
    selectedComponentSupplierId: selectedSupplier?.id || '',
    selectedSupplierId: selectedSupplier?.supplierId || '',
    selectedUnitPrice,
    lineTotal:
      selectedUnitPrice === null || pricingMethod !== 'perUnit'
        ? null
        : quantity * selectedUnitPrice,
  };
};

const validateComponentSupplierSelectionForWrite = (
  component,
  componentSuppliers,
  selectedComponentSupplierId
) => {
  const normalizedSelectionId = normalizeString(selectedComponentSupplierId);

  if (!normalizedSelectionId) {
    throw new Error('Component supplier selection is required.');
  }

  const selectedSupplier = (Array.isArray(componentSuppliers)
    ? componentSuppliers
    : []
  ).find((componentSupplier) => componentSupplier?.id === normalizedSelectionId);

  if (!selectedSupplier || selectedSupplier.componentId !== component?.id) {
    throw new Error('Selected supplier must belong to this component.');
  }

  if (!hasValidComponentSupplierCurrentPrice(selectedSupplier)) {
    throw new Error('Selected supplier must have a valid current price.');
  }

  return buildComponentSelectionPayload(
    {
      ...component,
      selectedComponentSupplierId: selectedSupplier.id,
      selectedSupplierId: selectedSupplier.supplierId,
    },
    componentSuppliers
  );
};

const buildEmptyComponentSelectionPayload = () => ({
  selectedComponentSupplierId: '',
  selectedSupplierId: '',
  selectedUnitPrice: null,
  lineTotal: null,
});

const buildComponentSupplierPricePayload = (data = {}) => {
  const parsedPrice = Number(data?.price);

  if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
    throw new Error('Price must be a positive number.');
  }

  return {
    price: parsedPrice,
    unit: normalizeString(data?.unit),
    pricingMethod: normalizeWritablePricingMethod(data?.pricingMethod),
    date: data?.date || new Date(),
    note: normalizeString(data?.note || data?.notes),
  };
};

const getProductSupplierRowsByProductId = async (productId) => {
  const normalizedProductId = normalizeString(productId);

  if (!normalizedProductId) {
    return [];
  }

  const q = query(
    collection(db, 'productSuppliers'),
    where('productId', '==', normalizedProductId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
    pricingMethod: normalizePricingMethod(docSnapshot.data()?.pricingMethod),
  }));
};

const getProductVariantRowsByProductId = async (productId) => {
  const normalizedProductId = normalizeString(productId);

  if (!normalizedProductId) {
    return [];
  }

  const q = query(
    collection(db, 'productVariants'),
    where('productId', '==', normalizedProductId),
    where('isActive', '==', true)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) =>
    normalizeProductVariant(docSnapshot)
  );
};

const buildSourceProductSupplierSelectionRows = (
  component = {},
  productSuppliers = []
) =>
  (Array.isArray(productSuppliers) ? productSuppliers : [])
    .map((productSupplier) => {
      const latestPriceEntry = sortPriceHistoryByDateDesc(
        productSupplier?.priceHistory
      )[0] || null;
      const price = Number(latestPriceEntry?.price);

      return {
        id: productSupplier?.id || '',
        productSupplierId: productSupplier?.id || '',
        componentId: component?.id || '',
        supplierId: normalizeString(productSupplier?.supplierId),
        currentPrice: Number.isFinite(price) ? price : null,
        unit: normalizeString(latestPriceEntry?.unit || productSupplier?.unit),
        pricingMethod: normalizePricingMethod(
          latestPriceEntry?.pricingMethod || productSupplier?.pricingMethod
        ),
        lastUpdated: latestPriceEntry?.date || productSupplier?.lastUpdated || null,
        notes: normalizeString(latestPriceEntry?.note || productSupplier?.notes),
        isActive: productSupplier?.isDeleted !== true,
        sourceType: 'productSupplier',
      };
    })
    .filter((componentSupplier) => componentSupplier.id)
    .filter((componentSupplier) => componentSupplier.supplierId);

const buildSourceProductVariantSelectionRows = (
  component = {},
  variants = [],
  productSuppliers = []
) => {
  const productSupplierMap = (Array.isArray(productSuppliers)
    ? productSuppliers
    : []
  ).reduce((lookup, productSupplier) => {
    const productSupplierId = normalizeString(productSupplier?.id);

    if (productSupplierId) {
      lookup[productSupplierId] = productSupplier;
    }

    return lookup;
  }, {});
  const selectedVariantName = normalizeString(component?.sourceVariantName);

  if (!selectedVariantName) {
    return [];
  }

  return (Array.isArray(variants) ? variants : [])
    .filter((variant) => normalizeString(variant?.variantName) === selectedVariantName)
    .map((variant) => {
      const productSupplier = productSupplierMap[variant.productSupplierId] ||
        (Array.isArray(productSuppliers) ? productSuppliers : []).find(
          (supplierRow) =>
            normalizeString(supplierRow?.supplierId) === variant.supplierId
        ) ||
        {};
      const supplierId = normalizeString(
        variant.supplierId || productSupplier?.supplierId
      );
      const price = Number(variant.currentPrice);

      return {
        id: variant.id || '',
        productVariantId: variant.id || '',
        productSupplierId: variant.productSupplierId || productSupplier?.id || '',
        componentId: component?.id || '',
        supplierId,
        currentPrice: Number.isFinite(price) ? price : null,
        unit: normalizeString(productSupplier?.unit),
        pricingMethod: normalizePricingMethod(productSupplier?.pricingMethod),
        lastUpdated: variant.lastUpdated || null,
        notes: normalizeString(variant.notes),
        isActive: variant.isActive === true,
        sourceType: 'productVariant',
        sourceVariantId: variant.id || component?.sourceVariantId || '',
        sourceVariantName: selectedVariantName,
      };
    })
    .filter((componentSupplier) => componentSupplier.id)
    .filter((componentSupplier) => componentSupplier.supplierId);
};

const getSelectableComponentSupplierRows = async (component = {}) => {
  const componentSuppliers = await getActiveComponentSuppliersByComponentId(
    component.id
  );

  if (!component?.sourceProductId) {
    return componentSuppliers;
  }

  const sourceProductSnapshot = await getDoc(
    doc(db, 'products', component.sourceProductId)
  );
  const sourceProduct = sourceProductSnapshot.exists()
    ? normalizeProduct(sourceProductSnapshot)
    : null;

  if (sourceProduct?.type === 'variant') {
    const [sourceProductSuppliers, sourceProductVariants] = await Promise.all([
      getProductSupplierRowsByProductId(component.sourceProductId),
      getProductVariantRowsByProductId(component.sourceProductId),
    ]);
    const sourceVariantSelectionRows = buildSourceProductVariantSelectionRows(
      component,
      sourceProductVariants,
      sourceProductSuppliers
    );

    return sourceVariantSelectionRows.length > 0
      ? sourceVariantSelectionRows
      : componentSuppliers;
  }

  const sourceProductSuppliers = await getProductSupplierRowsByProductId(
    component.sourceProductId
  );
  const sourceSelectionRows = buildSourceProductSupplierSelectionRows(
    component,
    sourceProductSuppliers
  );

  return sourceSelectionRows.length > 0
    ? sourceSelectionRows
    : componentSuppliers;
};

const addComponentPriceHistoryEntry = async ({
  componentSupplierId,
  componentId,
  supplierId,
  price,
  unit,
  pricingMethod,
  date,
  note,
}) => {
  await addDoc(collection(db, 'componentPriceHistory'), {
    componentSupplierId,
    componentId,
    supplierId,
    price,
    unit,
    pricingMethod: normalizePricingMethod(pricingMethod),
    date,
    note,
    createdAt: serverTimestamp(),
  });
};

const buildProductComponentPayload = (data = {}) => ({
  productId: normalizeString(data?.productId),
  name: normalizeString(data?.name),
  description: normalizeString(data?.description),
  quantity: normalizeNumber(data?.quantity, 1),
  unit: normalizeString(data?.unit),
  sourceProductId: normalizeString(data?.sourceProductId),
  sourceProductName: normalizeString(data?.sourceProductName),
  sourceVariantId: normalizeString(data?.sourceVariantId),
  sourceVariantName: normalizeString(data?.sourceVariantName),
  sortOrder: normalizeNumber(data?.sortOrder, 0),
  isRequired: data?.isRequired === true,
});

const normalizeProductComponentSourceVariantPayload = async (payload = {}) => {
  if (!payload.sourceProductId) {
    return {
      ...payload,
      sourceVariantId: '',
      sourceVariantName: '',
    };
  }

  const sourceProductSnapshot = await getDoc(doc(db, 'products', payload.sourceProductId));

  if (!sourceProductSnapshot.exists()) {
    return payload;
  }

  const sourceProduct = normalizeProduct(sourceProductSnapshot);

  if (sourceProduct.type !== 'variant') {
    return {
      ...payload,
      sourceVariantId: '',
      sourceVariantName: '',
    };
  }

  if (!payload.sourceVariantName) {
    throw new Error('Variant selection is required for variant component products.');
  }

  if (
    sourceProduct.allowedVariants.length > 0 &&
    !sourceProduct.allowedVariants.includes(payload.sourceVariantName)
  ) {
    throw new Error('Selected variant is not allowed for this product.');
  }

  if (payload.sourceVariantId) {
    return payload;
  }

  const sourceProductVariants = await getProductVariantRowsByProductId(
    payload.sourceProductId
  );
  const matchingVariant = sourceProductVariants.find(
    (variant) => variant.variantName === payload.sourceVariantName
  );

  return {
    ...payload,
    sourceVariantId: matchingVariant?.id || payload.sourceVariantName,
  };
};

export const getProductComponents = async (
  productId,
  { includeDeleted = false } = {}
) => {
  const normalizedProductId = normalizeString(productId);

  if (!normalizedProductId) {
    return [];
  }

  const q = query(
    collection(db, 'productComponents'),
    where('productId', '==', normalizedProductId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map((docSnapshot) => normalizeProductComponent(docSnapshot))
    .filter((component) => component.productId === normalizedProductId)
    .filter((component) =>
      includeDeleted === true
        ? component.isDeleted === true
        : component.isDeleted !== true
    )
    .sort((firstComponent, secondComponent) => {
      if (firstComponent.sortOrder !== secondComponent.sortOrder) {
        return firstComponent.sortOrder - secondComponent.sortOrder;
      }

      return firstComponent.name.localeCompare(secondComponent.name);
    });
};

export const createProductComponent = async (data = {}) => {
  const payload = await normalizeProductComponentSourceVariantPayload(
    buildProductComponentPayload(data)
  );

  if (!payload.name) {
    throw new Error('Component name is required.');
  }

  await validateMixedProduct(payload.productId);

  const docRef = await addDoc(collection(db, 'productComponents'), {
    ...(await withAuditActor({}, 'createdBy')),
    ...payload,
    ...buildEmptyComponentSelectionPayload(),
    isDeleted: false,
    deletedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return normalizeProductComponent({
    id: docRef.id,
    ...payload,
    ...buildEmptyComponentSelectionPayload(),
    isDeleted: false,
    deletedAt: null,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

export const updateProductComponent = async (id, data = {}) => {
  if (!id) {
    return null;
  }

  const currentComponent = await getProductComponentById(id);

  if (!currentComponent) {
    return null;
  }

  const payload = await normalizeProductComponentSourceVariantPayload(
    buildProductComponentPayload({
      ...currentComponent,
      ...data,
      productId: data?.productId || currentComponent.productId,
    })
  );

  if (!payload.name) {
    throw new Error('Component name is required.');
  }

  await validateMixedProduct(payload.productId);
  const nextComponent = {
    ...currentComponent,
    ...payload,
  };
  const selectableComponentSuppliers = await getSelectableComponentSupplierRows(
    nextComponent
  );
  const selectionPayload = currentComponent.selectedComponentSupplierId
    ? buildComponentSelectionPayload(
      nextComponent,
      selectableComponentSuppliers
    )
    : buildEmptyComponentSelectionPayload();

  await updateDoc(doc(db, 'productComponents', id), {
    ...(await withAuditActor({})),
    ...payload,
    ...selectionPayload,
    updatedAt: serverTimestamp(),
  });

  return getProductComponentById(id);
};

export const updateSelectedComponentSupplier = async (
  componentId,
  selectedComponentSupplierId
) => {
  const component = await validateComponentBelongsToMixedProduct(componentId);
  const componentSuppliers = await getSelectableComponentSupplierRows(component);
  const selection = validateComponentSupplierSelectionForWrite(
    component,
    componentSuppliers,
    selectedComponentSupplierId
  );

  await updateDoc(doc(db, 'productComponents', component.id), {
    ...(await withAuditActor({})),
    selectedComponentSupplierId: selection.selectedComponentSupplierId,
    selectedSupplierId: selection.selectedSupplierId,
    selectedUnitPrice: selection.selectedUnitPrice,
    lineTotal: selection.lineTotal,
    updatedAt: serverTimestamp(),
  });

  return getProductComponentById(component.id);
};

export const softDeleteProductComponent = async (id) => {
  if (!id) {
    return null;
  }

  const currentComponent = await getProductComponentById(id);

  if (!currentComponent) {
    return null;
  }

  await validateMixedProduct(currentComponent.productId);
  await updateDoc(doc(db, 'productComponents', id), {
    ...(await withAuditActor({})),
    isDeleted: true,
    deletedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { success: true };
};

export const restoreProductComponent = async (id) => {
  if (!id) {
    return null;
  }

  const currentComponent = await getProductComponentById(id);

  if (!currentComponent) {
    return null;
  }

  await validateMixedProduct(currentComponent.productId);
  await updateDoc(doc(db, 'productComponents', id), {
    ...(await withAuditActor({})),
    isDeleted: false,
    deletedAt: null,
    updatedAt: serverTimestamp(),
  });

  return getProductComponentById(id);
};

export const getComponentSuppliers = async (componentId) => {
  const normalizedComponentId = normalizeString(componentId);

  if (!normalizedComponentId) {
    return [];
  }

  const q = query(
    collection(db, 'componentSuppliers'),
    where('componentId', '==', normalizedComponentId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs
    .map((docSnapshot) => normalizeComponentSupplier(docSnapshot))
    .filter(
      (componentSupplier) =>
        componentSupplier.componentId === normalizedComponentId
    )
    .filter((componentSupplier) => componentSupplier.isActive === true);
};

export const createComponentSupplierPrice = async (
  componentId,
  supplierId,
  data = {}
) => {
  const component = await validateComponentBelongsToMixedProduct(componentId);
  const normalizedSupplierId = await validateSupplierExists(supplierId);
  const existingComponentSupplier =
    await getActiveComponentSupplierByComponentAndSupplierId(
      component.id,
      normalizedSupplierId
    );

  if (existingComponentSupplier) {
    throw new Error('Supplier already exists for this component.');
  }

  const pricePayload = buildComponentSupplierPricePayload(data);
  const payload = {
    componentId: component.id,
    supplierId: normalizedSupplierId,
    currentPrice: pricePayload.price,
    unit: pricePayload.unit,
    pricingMethod: pricePayload.pricingMethod,
    lastUpdated: pricePayload.date,
    notes: pricePayload.note,
    isActive: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  const docRef = await addDoc(collection(db, 'componentSuppliers'), payload);

  await addComponentPriceHistoryEntry({
    componentSupplierId: docRef.id,
    componentId: component.id,
    supplierId: normalizedSupplierId,
    ...pricePayload,
  });

  return normalizeComponentSupplier({
    id: docRef.id,
    ...payload,
    createdAt: new Date(),
    updatedAt: new Date(),
  });
};

export const updateComponentSupplierPrice = async (
  componentSupplierId,
  data = {}
) => {
  const normalizedComponentSupplierId = normalizeString(componentSupplierId);

  if (!normalizedComponentSupplierId) {
    return null;
  }

  const componentSupplierSnapshot = await getDoc(
    doc(db, 'componentSuppliers', normalizedComponentSupplierId)
  );

  if (!componentSupplierSnapshot.exists()) {
    return null;
  }

  const currentComponentSupplier = normalizeComponentSupplier(componentSupplierSnapshot);
  const component = await validateComponentBelongsToMixedProduct(
    currentComponentSupplier.componentId
  );
  const normalizedSupplierId = await validateSupplierExists(
    currentComponentSupplier.supplierId
  );
  const pricePayload = buildComponentSupplierPricePayload(data);
  const priceChanged = Number(currentComponentSupplier.currentPrice) !== pricePayload.price;

  await updateDoc(doc(db, 'componentSuppliers', normalizedComponentSupplierId), {
    currentPrice: pricePayload.price,
    unit: pricePayload.unit,
    pricingMethod: pricePayload.pricingMethod,
    lastUpdated: pricePayload.date,
    notes: pricePayload.note,
    isActive: true,
    updatedAt: serverTimestamp(),
  });

  if (
    component.selectedComponentSupplierId === normalizedComponentSupplierId ||
    (
      !component.selectedComponentSupplierId &&
      component.selectedSupplierId === normalizedSupplierId
    )
  ) {
    const lineTotal =
      pricePayload.pricingMethod === 'perUnit'
        ? component.quantity * pricePayload.price
        : null;

    await updateDoc(doc(db, 'productComponents', component.id), {
      selectedComponentSupplierId: normalizedComponentSupplierId,
      selectedSupplierId: normalizedSupplierId,
      selectedUnitPrice: pricePayload.price,
      lineTotal,
      updatedAt: serverTimestamp(),
    });
  }

  if (priceChanged) {
    await addComponentPriceHistoryEntry({
      componentSupplierId: normalizedComponentSupplierId,
      componentId: component.id,
      supplierId: normalizedSupplierId,
      ...pricePayload,
    });
  }

  const updatedSnapshot = await getDoc(
    doc(db, 'componentSuppliers', normalizedComponentSupplierId)
  );

  return updatedSnapshot.exists()
    ? normalizeComponentSupplier(updatedSnapshot)
    : null;
};

export const getComponentPriceHistory = async (componentSupplierId) => {
  const normalizedComponentSupplierId = normalizeString(componentSupplierId);

  if (!normalizedComponentSupplierId) {
    return [];
  }

  const q = query(
    collection(db, 'componentPriceHistory'),
    where('componentSupplierId', '==', normalizedComponentSupplierId)
  );
  const querySnapshot = await getDocs(q);

  return sortPriceHistoryByDateDesc(
    querySnapshot.docs.map((docSnapshot) =>
      normalizeComponentPriceHistoryEntry(docSnapshot)
    ).filter(
      (entry) =>
        entry.componentSupplierId === normalizedComponentSupplierId
    )
  );
};

const validateProductVariantName = async (productId, variantName) => {
  const productSnapshot = await getDoc(doc(db, 'products', productId));

  if (!productSnapshot.exists()) {
    throw new Error('Product not found.');
  }

  const product = normalizeProduct(productSnapshot);

  if (product.type !== 'variant') {
    throw new Error('Variants can only be added to variant products.');
  }

  if (product.allowedVariants.length === 0) {
    throw new Error('Product has no allowed variants.');
  }

  if (!product.allowedVariants.includes(variantName)) {
    throw new Error('Variant is not allowed for this product.');
  }

  return product;
};

const validateProductSupplierCompatibleProduct = async (productId) => {
  const normalizedProductId = normalizeString(productId);

  if (!normalizedProductId) {
    throw new Error('Product is required.');
  }

  const productSnapshot = await getDoc(doc(db, 'products', normalizedProductId));

  if (
    productSnapshot.exists() &&
    normalizeProduct(productSnapshot).type === 'mixed'
  ) {
    throw new Error('Mixed products do not use product suppliers.');
  }
};

export const createVariant = async (productId, data = {}) => {
  const variantName = normalizeString(data?.variantName || data?.name);
  const supplierId = normalizeString(data?.supplierId);
  const productSupplierId = normalizeString(data?.productSupplierId);
  const notes = normalizeString(data?.notes || data?.note);

  if (!productId || !variantName) {
    throw new Error('Product and variant name are required.');
  }

  await validateProductVariantName(productId, variantName);

  if (supplierId) {
    const existingVariantQuery = query(
      collection(db, 'productVariants'),
      where('productId', '==', productId),
      where('supplierId', '==', supplierId),
      where('variantName', '==', variantName),
      where('isActive', '==', true),
      limit(1)
    );
    const existingVariantSnapshot = await getDocs(existingVariantQuery);

    if (!existingVariantSnapshot.empty) {
      return normalizeProductVariant(existingVariantSnapshot.docs[0]);
    }
  }

  const payload = {
    productId,
    ...(supplierId ? { supplierId } : {}),
    ...(productSupplierId ? { productSupplierId } : {}),
    variantName,
    name: variantName,
    specs: normalizeSpecs(data?.specs),
    notes,
    currentPrice: null,
    lastUpdated: null,
    priceHistory: [],
    isActive: true,
    createdAt: serverTimestamp(),
  };
  const docRef = await addDoc(
    collection(db, 'productVariants'),
    await withAuditActor(payload, 'createdBy')
  );

  return normalizeProductVariant({
    id: docRef.id,
    ...payload,
    createdAt: new Date(),
  });
};

export const createProductVariant = createVariant;

export const updateVariant = async (variantId, data = {}) => {
  if (!variantId) {
    return null;
  }

  const variantName = normalizeString(data?.variantName || data?.name);
  const currentSnapshot = await getDoc(doc(db, 'productVariants', variantId));

  if (!currentSnapshot.exists()) {
    return null;
  }

  const currentVariant = normalizeProductVariant(currentSnapshot);
  const productId = normalizeString(data?.productId || currentVariant.productId);

  if (!variantName || !productId) {
    throw new Error('Product and variant name are required.');
  }

  const currentVariantName = normalizeString(
    currentVariant.variantName || currentVariant.name
  );

  if (variantName !== currentVariantName) {
    await validateProductVariantName(productId, variantName);
  } else {
    const productSnapshot = await getDoc(doc(db, 'products', productId));

    if (!productSnapshot.exists() || normalizeProduct(productSnapshot).type !== 'variant') {
      throw new Error('Variants can only be updated for variant products.');
    }
  }

  await updateDoc(doc(db, 'productVariants', variantId), await withAuditActor({
    variantName,
    name: variantName,
    specs: normalizeSpecs(data?.specs),
    notes: normalizeString(data?.notes || data?.note),
    updatedAt: serverTimestamp(),
  }));

  const updatedSnapshot = await getDoc(doc(db, 'productVariants', variantId));

  if (!updatedSnapshot.exists()) {
    return null;
  }

  return normalizeProductVariant(updatedSnapshot);
};

export const setVariantPrice = async (variantId, newEntry = {}) => {
  if (!variantId) {
    return null;
  }

  const currentSnapshot = await getDoc(doc(db, 'productVariants', variantId));

  if (!currentSnapshot.exists()) {
    return null;
  }

  const currentVariant = normalizeProductVariant(currentSnapshot);
  const variantName = normalizeString(currentVariant.variantName || currentVariant.name);
  const parsedPrice = Number(newEntry.price);
  const date = newEntry.date || new Date();
  const note = normalizeString(newEntry.note || newEntry.notes);
  const pricingMethod = normalizeWritablePricingMethod(newEntry?.pricingMethod);

  if (!variantName || !Number.isFinite(parsedPrice) || parsedPrice <= 0) {
    return null;
  }

  await updateDoc(doc(db, 'productVariants', variantId), await withAuditActor({
    variantName,
    name: variantName,
    currentPrice: parsedPrice,
    lastUpdated: date,
    notes: note,
  }));
  await addDoc(collection(db, 'priceHistory'), await withAuditActor({
    variantId,
    price: parsedPrice,
    pricingMethod,
    date,
    note,
    createdAt: serverTimestamp(),
  }, 'createdBy'));

  const updatedSnapshot = await getDoc(doc(db, 'productVariants', variantId));

  if (!updatedSnapshot.exists()) {
    return null;
  }

  return normalizeProductVariant(updatedSnapshot);
};

export const addProductVariantPrice = setVariantPrice;

export const getVariantPriceHistory = async (variantId, fallbackHistory = []) => {
  if (!variantId) {
    return sortPriceHistoryByDateDesc(fallbackHistory);
  }

  const q = query(
    collection(db, 'priceHistory'),
    where('variantId', '==', variantId)
  );
  const querySnapshot = await getDocs(q);
  const collectionHistory = querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  }));

  if (collectionHistory.length > 0) {
    return sortPriceHistoryByDateDesc(collectionHistory);
  }

  return sortPriceHistoryByDateDesc(fallbackHistory);
};

export const deleteVariant = async (variantId) => {
  if (!variantId) {
    return { success: false };
  }

  await updateDoc(doc(db, 'productVariants', variantId), await withAuditActor({
    isActive: false,
    lastUpdated: serverTimestamp(),
  }));

  return { success: true };
};

export const removeProductVariant = deleteVariant;

export const createProduct = async (data) => {
  const payload = {
    ...buildProductPayload(data, { includeCreatedAt: true }),
    isDeleted: false,
    deletedAt: null,
  };
  const docRef = await addDoc(
    collection(db, 'products'),
    await withAuditActor(payload, 'createdBy')
  );

  return {
    id: docRef.id,
    ...normalizeProduct({
      id: docRef.id,
      ...payload,
      createdAt: new Date(),
      updatedAt: new Date(),
    }),
  };
};

export const updateProduct = async (id, data) => {
  const payload = buildProductPayload(data, { removeLegacyFields: true });

  await updateDoc(doc(db, 'products', id), await withAuditActor(payload));

  const updatedSnapshot = await getDoc(doc(db, 'products', id));

  if (!updatedSnapshot.exists()) {
    return null;
  }

  return normalizeProduct(updatedSnapshot);
};

export const softDeleteProduct = async (id) => {
  await updateDoc(doc(db, 'products', id), await withAuditActor({
    isDeleted: true,
    deletedAt: serverTimestamp(),
  }));
};

export const restoreProduct = async (id) => {
  await updateDoc(doc(db, 'products', id), await withAuditActor({
    isDeleted: false,
    deletedAt: null,
  }));
};

export const permanentlyDeleteProduct = async (id) => {
  await cascadeDeleteProductRelations(id);

  await deleteDoc(doc(db, 'products', id));
};

const getProductRelationSnapshots = async (productId) => {
  if (!productId) {
    return {
      productSupplierDocs: [],
      productVariantDocs: [],
      variantPriceHistoryDocs: [],
      productComponentDocs: [],
      componentSupplierDocs: [],
      componentPriceHistoryDocs: [],
      mixedProductComponentDocs: [],
      bundleDocs: [],
      bundleComponentDocs: [],
      bundleExtraCostDocs: [],
    };
  }

  const [
    productSupplierSnapshot,
    productVariantSnapshot,
    productComponentSnapshot,
    sourceProductComponentSnapshot,
    mixedProductComponentSnapshot,
    bundleSnapshot,
  ] = await Promise.all([
    getDocs(
      query(
        collection(db, 'productSuppliers'),
        where('productId', '==', productId)
      )
    ),
    getDocs(
      query(
        collection(db, 'productVariants'),
        where('productId', '==', productId)
      )
    ),
    getDocs(
      query(
        collection(db, 'productComponents'),
        where('productId', '==', productId)
      )
    ),
    getDocs(
      query(
        collection(db, 'productComponents'),
        where('sourceProductId', '==', productId)
      )
    ),
    getDocs(
      query(
        collection(db, 'mixedProductComponents'),
        where('productId', '==', productId)
      )
    ),
    getDocs(
      query(
        collection(db, 'bundles'),
        where('productId', '==', productId)
      )
    ),
  ]);
  const productVariantDocs = productVariantSnapshot.docs;
  const productComponentDocs = Array.from(
    new Map(
      [
        ...productComponentSnapshot.docs,
        ...sourceProductComponentSnapshot.docs,
      ].map((docSnapshot) => [docSnapshot.ref.path, docSnapshot])
    ).values()
  );
  const mixedProductComponentDocs = mixedProductComponentSnapshot.docs;
  const bundleDocs = bundleSnapshot.docs;
  const bundleComponentByBundleSnapshots = await Promise.all(
    bundleDocs.map((bundleDoc) =>
      getDocs(
        query(
          collection(db, 'bundleComponents'),
          where('bundleId', '==', bundleDoc.id)
        )
      )
    )
  );
  const bundleComponentBySourceSnapshots = await Promise.all(
    [...bundleDocs, ...mixedProductComponentDocs].map((sourceDoc) =>
      getDocs(
        query(
          collection(db, 'bundleComponents'),
          where('sourceId', '==', sourceDoc.id)
        )
      )
    )
  );
  const bundleComponentDocs = Array.from(
    new Map(
      [
        ...bundleComponentByBundleSnapshots.flatMap((snapshot) => snapshot.docs),
        ...bundleComponentBySourceSnapshots.flatMap((snapshot) => snapshot.docs),
      ].map((docSnapshot) => [docSnapshot.ref.path, docSnapshot])
    ).values()
  );
  const bundleExtraCostSnapshots = await Promise.all(
    bundleDocs.map((bundleDoc) =>
      getDocs(
        query(
          collection(db, 'bundleExtraCosts'),
          where('bundleId', '==', bundleDoc.id)
        )
      )
    )
  );
  const variantPriceHistorySnapshots = await Promise.all(
    productVariantDocs.map((variantDoc) =>
      getDocs(
        query(
          collection(db, 'priceHistory'),
          where('variantId', '==', variantDoc.id)
        )
      )
    )
  );
  const componentSupplierSnapshots = await Promise.all(
    productComponentDocs.map((componentDoc) =>
      getDocs(
        query(
          collection(db, 'componentSuppliers'),
          where('componentId', '==', componentDoc.id)
        )
      )
    )
  );
  const componentSupplierDocs = componentSupplierSnapshots.flatMap(
    (snapshot) => snapshot.docs
  );
  const componentPriceHistorySnapshots = await Promise.all(
    componentSupplierDocs.map((componentSupplierDoc) =>
      getDocs(
        query(
          collection(db, 'componentPriceHistory'),
          where('componentSupplierId', '==', componentSupplierDoc.id)
        )
      )
    )
  );

  return {
    productSupplierDocs: productSupplierSnapshot.docs,
    productVariantDocs,
    variantPriceHistoryDocs: variantPriceHistorySnapshots.flatMap(
      (snapshot) => snapshot.docs
    ),
    productComponentDocs,
    componentSupplierDocs,
    componentPriceHistoryDocs: componentPriceHistorySnapshots.flatMap(
      (snapshot) => snapshot.docs
    ),
    mixedProductComponentDocs,
    bundleDocs,
    bundleComponentDocs,
    bundleExtraCostDocs: bundleExtraCostSnapshots.flatMap(
      (snapshot) => snapshot.docs
    ),
  };
};

const getProductDeletionImpactFromSnapshots = ({
  productSupplierDocs = [],
  productVariantDocs = [],
  variantPriceHistoryDocs = [],
  productComponentDocs = [],
  componentSupplierDocs = [],
  componentPriceHistoryDocs = [],
  mixedProductComponentDocs = [],
  bundleDocs = [],
  bundleComponentDocs = [],
  bundleExtraCostDocs = [],
}) => {
  const supplierIds = new Set();
  const productSupplierById = new Map();
  const supplierPriceCount = productSupplierDocs.reduce((total, docSnapshot) => {
    const data = docSnapshot.data() || {};

    productSupplierById.set(docSnapshot.id, data);

    if (data.supplierId) {
      supplierIds.add(data.supplierId);
    }

    return total + (Array.isArray(data.priceHistory) ? data.priceHistory.length : 0);
  }, 0);
  const embeddedVariantPriceCount = productVariantDocs.reduce((total, docSnapshot) => {
    const data = docSnapshot.data() || {};
    const supplierId =
      data.supplierId || productSupplierById.get(data.productSupplierId)?.supplierId;

    if (supplierId) {
      supplierIds.add(supplierId);
    }

    return total + (Array.isArray(data.priceHistory) ? data.priceHistory.length : 0);
  }, 0);
  const variantPriceCount =
    variantPriceHistoryDocs.length + embeddedVariantPriceCount;
  componentSupplierDocs.forEach((docSnapshot) => {
    const data = docSnapshot.data() || {};

    if (data.supplierId) {
      supplierIds.add(data.supplierId);
    }
  });
  const componentPriceCount = componentPriceHistoryDocs.length;

  return {
    supplierCount: supplierIds.size,
    supplierPriceCount,
    variantPriceCount,
    componentPriceCount,
    productLinkCount: productSupplierDocs.length,
    variantRecordCount: productVariantDocs.length,
    componentRecordCount: productComponentDocs.length,
    componentSupplierCount: componentSupplierDocs.length,
    mixedProductComponentRecordCount: mixedProductComponentDocs.length,
    configurationRecordCount: bundleDocs.length,
    configurationComponentRecordCount: bundleComponentDocs.length,
    configurationExtraCostRecordCount: bundleExtraCostDocs.length,
    priceCount: supplierPriceCount + variantPriceCount + componentPriceCount,
  };
};

export const getProductDeletionImpact = async (productId) => {
  const snapshots = await getProductRelationSnapshots(productId);

  return getProductDeletionImpactFromSnapshots(snapshots);
};

const deleteDocumentRefs = async (documentRefs) => {
  const uniqueRefs = Array.from(
    new Map(documentRefs.map((documentRef) => [documentRef.path, documentRef])).values()
  );
  const batchSize = 450;

  for (let index = 0; index < uniqueRefs.length; index += batchSize) {
    const batch = writeBatch(db);
    const refs = uniqueRefs.slice(index, index + batchSize);

    refs.forEach((documentRef) => batch.delete(documentRef));
    await batch.commit();
  }
};

const cascadeDeleteProductRelations = async (productId) => {
  const {
    productSupplierDocs,
    productVariantDocs,
    variantPriceHistoryDocs,
    productComponentDocs,
    componentSupplierDocs,
    componentPriceHistoryDocs,
    mixedProductComponentDocs,
    bundleDocs,
    bundleComponentDocs,
    bundleExtraCostDocs,
  } = await getProductRelationSnapshots(productId);

  await deleteDocumentRefs([
    ...bundleExtraCostDocs.map((docSnapshot) => docSnapshot.ref),
    ...bundleComponentDocs.map((docSnapshot) => docSnapshot.ref),
    ...bundleDocs.map((docSnapshot) => docSnapshot.ref),
    ...mixedProductComponentDocs.map((docSnapshot) => docSnapshot.ref),
    ...componentPriceHistoryDocs.map((docSnapshot) => docSnapshot.ref),
    ...componentSupplierDocs.map((docSnapshot) => docSnapshot.ref),
    ...productComponentDocs.map((docSnapshot) => docSnapshot.ref),
    ...variantPriceHistoryDocs.map((docSnapshot) => docSnapshot.ref),
    ...productVariantDocs.map((docSnapshot) => docSnapshot.ref),
    ...productSupplierDocs.map((docSnapshot) => docSnapshot.ref),
  ]);
};

export const getProductSuppliers = async (productId) => {
  if (!productId) {
    return [];
  }

  const q = query(
    collection(db, 'productSuppliers'),
    where('productId', '==', productId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
    pricingMethod: normalizePricingMethod(docSnapshot.data()?.pricingMethod),
  }));
};

export const getProductSuppliersBySupplierId = async (supplierId) => {
  if (!supplierId) {
    return [];
  }

  const q = query(
    collection(db, 'productSuppliers'),
    where('supplierId', '==', supplierId)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
    pricingMethod: normalizePricingMethod(docSnapshot.data()?.pricingMethod),
  }));
};

export const getLatestPriceEntry = (priceHistory) => {
  if (!Array.isArray(priceHistory) || priceHistory.length === 0) {
    return null;
  }

  return sortPriceHistoryByDateDesc(priceHistory)[0] || null;
};

export const createOrUpdateProductSupplier = async (
  productId,
  supplierId,
  newEntry
) => {
  await validateProductSupplierCompatibleProduct(productId);
  const pricingMethod = normalizeWritablePricingMethod(newEntry?.pricingMethod);
  const priceEntry = {
    ...newEntry,
    pricingMethod,
  };

  const q = query(
    collection(db, 'productSuppliers'),
    where('productId', '==', productId),
    where('supplierId', '==', supplierId)
  );
  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const existingDoc = querySnapshot.docs[0];
    const existingData = existingDoc.data();
    const currentHistory = Array.isArray(existingData.priceHistory)
      ? existingData.priceHistory
      : [];

    await updateDoc(existingDoc.ref, await withAuditActor({
      pricingMethod,
      priceHistory: [...currentHistory, priceEntry],
    }));

    return {
      id: existingDoc.id,
      ...existingData,
      pricingMethod,
      priceHistory: [...currentHistory, priceEntry],
    };
  }

  const payload = {
    productId,
    supplierId,
    pricingMethod,
    priceHistory: [priceEntry],
  };
  const docRef = await addDoc(
    collection(db, 'productSuppliers'),
    await withAuditActor(payload, 'createdBy')
  );

  return {
    id: docRef.id,
    ...payload,
  };
};

export const addProductSupplier = async (productId, supplierId, data = {}) => {
  await validateProductSupplierCompatibleProduct(productId);
  const hasPricingMethod = Object.prototype.hasOwnProperty.call(
    data || {},
    'pricingMethod'
  );
  const pricingMethod = hasPricingMethod
    ? normalizeWritablePricingMethod(data?.pricingMethod)
    : DEFAULT_PRICING_METHOD;

  const q = query(
    collection(db, 'productSuppliers'),
    where('productId', '==', productId),
    where('supplierId', '==', supplierId)
  );
  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const existingDoc = querySnapshot.docs[0];
    const existingData = existingDoc.data();

    if (hasPricingMethod) {
      await updateDoc(existingDoc.ref, await withAuditActor({
        pricingMethod,
      }));
    }

    return {
      exists: true,
      id: existingDoc.id,
      ...existingData,
      pricingMethod: hasPricingMethod
        ? pricingMethod
        : normalizePricingMethod(existingData?.pricingMethod),
    };
  }

  const payload = {
    productId,
    supplierId,
    pricingMethod,
    priceHistory: [],
    createdAt: new Date(),
  };
  const docRef = await addDoc(
    collection(db, 'productSuppliers'),
    await withAuditActor(payload, 'createdBy')
  );

  return { success: true, id: docRef.id, ...payload };
};

export const deleteProductSupplier = async (productId, supplierId) => {
  if (!productId || !supplierId) {
    return { success: false, deleted: false };
  }

  const q = query(
    collection(db, 'productSuppliers'),
    where('productId', '==', productId),
    where('supplierId', '==', supplierId)
  );
  const querySnapshot = await getDocs(q);

  if (querySnapshot.empty) {
    return { success: true, deleted: false };
  }

  const existingDoc = querySnapshot.docs[0];
  await deleteItem('productSuppliers', existingDoc.id);

  return {
    success: true,
    deleted: true,
    id: existingDoc.id,
  };
};

export const getDeletedCategories = async () => {
  const q = query(collection(db, 'categories'), where('isDeleted', '==', true));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map((docSnapshot) => ({
    id: docSnapshot.id,
    ...docSnapshot.data(),
  }));
};

export const createCategory = async (data) => {
  const payload = {
    ...data,
    slug: generateSlug(data.name),
    isDeleted: false,
    deletedAt: null,
    createdAt: new Date(),
  };

  return addDoc(
    collection(db, 'categories'),
    await withAuditActor(payload, 'createdBy')
  );
};

export const updateCategory = async (id, data) => {
  const updates = {
    ...data,
  };

  if (data.name) {
    updates.slug = generateSlug(data.name);
  }

  await updateDoc(doc(db, 'categories', id), await withAuditActor(updates));
};

export const softDeleteCategory = async (id) => {
  await updateItemStatus('categories', id, await withAuditActor({
    isDeleted: true,
    deletedAt: new Date(),
  }));
};

export const restoreCategory = async (id) => {
  await updateItemStatus('categories', id, await withAuditActor({
    isDeleted: false,
    deletedAt: null,
  }));
};

export const permanentlyDeleteCategory = async (id) => {
  const deleteCategory = httpsCallable(functions, 'deleteCategory');
  const result = await deleteCategory({ categoryId: id, permanent: true });

  return result.data;
};
